import { PlusOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import TreeBranchLineIcon from '@/assets/icons/TreeBranchLineIcon';
import { Flex, Space } from '@/components/base';
import Button from '@/components/Button/Button';
import ColorSelector from '@/components/Common/ColorSelector';
import Input from '@/components/Inputs/Input';
import Typo from '@/components/Typography/Typo';
import useColor from '@/hooks/domain/useColor';
import { useInputs } from '@/hooks/pure/useInputs';
import useStates from '@/hooks/pure/useStates';
import useToastStore from '@/stores/pure/ToastStore';
import useTagStore from '@/stores/TagStore';
import mockupUtils from '@/utils/mockupUtils';
import { isMac } from '@/utils/util';

import { validateTagNameProperty } from './tagInputValidator';

type Props = {
  parentTag: TagT;
  isChildBeingAdded: boolean;
  setIsChildBeingAdded: (status: boolean) => void;
};

const NewTagItem = ({
  parentTag,
  isChildBeingAdded,
  setIsChildBeingAdded,
}: Props) => {
  const { alias: parentTagAlias, level: parentLevel } = parentTag;
  const addDefaultToast = useToastStore((state) => state.addDefaultToast);

  const [tagNameError, setTagNameError] = useState<string | null>(null);
  const { state, setValue } = useStates<{
    isEdit: boolean;
    isLoading: boolean;
  }>({
    isEdit: false,
    isLoading: false,
  });

  const color = useColor();
  const { tagMap, addTag } = useTagStore((state) => ({
    tagMap: state.tagMap,
    addTag: state.addTag,
  }));

  useEffect(() => {
    if (isChildBeingAdded) setValue('isEdit', true);
  }, [isChildBeingAdded, setValue]);

  const [inputs, onChange, onCustomChange, initializeInputs] = useInputs({
    name: '',
    color: mockupUtils.getRandomInteger(0, 12) + 1,
  });

  const onSaveHandler = async () => {
    if (!inputs.name) return;
    if (tagNameError) return;
    setValue('isLoading', true);
    try {
      await addTag(parentTagAlias, inputs.name, inputs.color, false);
      setValue('isEdit', false);
    } catch (e) {
      addDefaultToast('Creating Tag failed', 'fail');
    } finally {
      setIsChildBeingAdded(false);
      setValue('isLoading', false);
      initializeInputs();
    }
  };

  const handleTagNameInput = (e: { target: { name: string; value: any } }) => {
    const { value } = e.target;
    const siblingAliases = parentTag.children.map(
      (alias) => tagMap[alias].name,
    );
    const errorMessage = validateTagNameProperty(value, siblingAliases);
    if (errorMessage) setTagNameError(errorMessage);
    else setTagNameError(null);
    onChange(e);
  };

  if (state.isEdit) {
    return (
      <Style a="center" isNew={false} isActive={false}>
        <Col g="8px" a="center" style={{ flexGrow: 8 }}>
          <Space w={36 * parentTag.level} />
          {parentTag.level > 0 && (
            <IconsWrapper a="center" j="space-around" d="row">
              <TreeBranchLineIcon />
            </IconsWrapper>
          )}
          <ColorSelector
            value={inputs.color}
            onChange={(v) => onCustomChange('color', v)}
          />
          <Flex style={{ width: '40%' }}>
            <Input
              name="name"
              sizeType="m"
              placeholder="type tag name"
              value={inputs.name}
              onChange={(e) => handleTagNameInput(e)}
              onKeyDown={(e) => {
                if (isMac()) {
                  if (e.metaKey && e.key === 'Enter') {
                    onSaveHandler();
                  }
                } else {
                  if (e.ctrlKey && e.key === 'Enter') {
                    onSaveHandler();
                  }
                }
              }}
            />
          </Flex>
          {tagNameError && (
            <Typo type="label3" color={color.red700}>
              {tagNameError}
            </Typo>
          )}
        </Col>
        <Col j="flex-end" g="8px" style={{ flexGrow: 2 }}>
          <Button
            onClick={() => {
              setValue('isEdit', false);
              setIsChildBeingAdded(false);
              setTagNameError(null);
              initializeInputs();
            }}
            disabled={state.isLoading}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button onClick={onSaveHandler} disabled={state.isLoading}>
            Save
          </Button>
        </Col>
      </Style>
    );
  }

  if (parentLevel === 0)
    return (
      <Style
        g="8px"
        a="center"
        isNew={true}
        isActive={!isChildBeingAdded}
        style={{ height: 40 }}
        onClick={() => {
          if (isChildBeingAdded) return;
          setValue('isEdit', true);
          setIsChildBeingAdded(true);
        }}
      >
        <PlusOutlined />
        <Typo type="label2">New tag</Typo>
      </Style>
    );

  return null;
};

const Style = styled(Flex)<{ isActive: boolean; isNew: boolean }>`
  padding: 8px 20px;
  color: ${(props) => props.theme.colors.gray600};
  cursor: ${(props) => props.isActive && props.isNew && 'pointer'};
`;

const Col = styled(Flex)`
  flex: 1;
  padding-right: 8px;
`;

const IconsWrapper = styled(Flex)`
  width: 20px;
  height: 20px;
  color: ${({ theme }) => theme.colors.gray600};
`;

export default NewTagItem;
